/**
 * Cora — what the CV screening assistant did, how good she is, and whether she is healthy.
 *
 * Four tabs, one per question TA asks:
 *   Screenings  — every candidate Cora screened: her verdict, the note she wrote, what happened next.
 *   Scorecards  — Kiki's scorecard drafts for new roles, and roles still waiting for one.
 *   Quality     — agreement with the old (Relevance) Cora during the shadow phase, whether recruiters
 *                 follow her verdicts, and where her own rules and her verdict disagree.
 *   Operations  — mode, configuration, every hourly run, positions that could not be screened.
 *
 * One path per tab (/cora/:tab) so a tab is linkable, like the TA Action Boards.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsList, TabsTrigger } from '@finn-auto/design-system/atoms/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { fetchCoraRuns, fetchCoraStatus, type CoraMode } from '../../services/coraApi';
import ScreeningsTab from './ScreeningsTab';
import QualityTab from './QualityTab';
import OperationsTab from './OperationsTab';
import ScorecardsTab from './ScorecardsTab';
import RunDetail from './RunDetail';

const TABS = ['screenings', 'scorecards', 'quality', 'operations'] as const;
type Tab = (typeof TABS)[number];
const DAY_OPTIONS = [7, 30, 90, 180];

const MODE_TEXT: Record<CoraMode, { label: string; className: string; help: string }> = {
  off: { label: 'Off', className: 'border-pearl text-zinc-600', help: 'Cora is not screening.' },
  shadow: {
    label: 'Shadow',
    className: 'border-orange text-orange',
    help: 'Cora assesses every new applicant but writes nothing to Lever yet — the Relevance agent still does. Compare the two on the Quality tab.',
  },
  live: { label: 'Live', className: 'border-green text-green', help: 'Cora writes her note and tags to Lever.' },
};

export default function CoraView() {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'screenings';
  const [days, setDays] = useState(30);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const status = useQuery({ queryKey: ['cora', 'status'], queryFn: fetchCoraStatus, staleTime: 60_000 });
  const runs = useQuery({ queryKey: ['cora', 'runs', days], queryFn: () => fetchCoraRuns(days), staleTime: 60_000 });

  useEffect(() => {
    void logAnalyticsEvent('cora_viewed', { tab });
  }, [tab]);

  const openRun = (id: string, source: string) => {
    setOpenRunId(id);
    void logAnalyticsEvent('cora_run_opened', { source });
  };
  const mode = status.data ? MODE_TEXT[status.data.mode] : null;

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-3 text-xl font-semibold text-zinc-900">
              🔎 Cora — CV screening
              {mode && (
                <span className={`rounded-sm border border-solid px-2 py-0.5 text-xs font-medium ${mode.className}`}>
                  {mode.label}
                </span>
              )}
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-zinc-600">
              Cora reads every new applicant&apos;s CV against the position&apos;s scoring document and leaves a briefing in
              Lever. She never rejects, archives or decides. {mode?.help}
            </p>
          </div>
          <div className="w-40">
            <Select
              value={String(days)}
              onValueChange={(next: string) => {
                setDays(Number(next));
                void logAnalyticsEvent('cora_period_changed', { days: Number(next) });
              }}
            >
              <SelectTrigger aria-label="Period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    Last {d} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={tab} onValueChange={(next: string) => navigate(`/cora/${next}`)}>
          <TabsList className="h-auto w-full justify-start gap-1 border-b border-solid border-pearl bg-transparent p-0 pb-3">
            <TabsTrigger value="screenings" className="rounded px-4 py-2">Screenings</TabsTrigger>
            <TabsTrigger value="scorecards" className="rounded px-4 py-2">Scorecards</TabsTrigger>
            <TabsTrigger value="quality" className="rounded px-4 py-2">Quality</TabsTrigger>
            <TabsTrigger value="operations" className="rounded px-4 py-2">Operations</TabsTrigger>
          </TabsList>
        </Tabs>

        {runs.isError && (
          <p role="alert" className="rounded-sm border border-solid border-red p-3 text-sm text-red">
            Cora&apos;s runs could not be loaded: {(runs.error as Error).message}
          </p>
        )}
        {runs.isLoading && <p className="text-sm text-zinc-600">Loading Cora&apos;s runs…</p>}

        {runs.data && tab === 'screenings' && (
          <ScreeningsTab runs={runs.data.runs} mode={status.isSuccess ? status.data.mode : null} onOpen={(id) => openRun(id, 'screenings')} />
        )}
        {tab === 'scorecards' && <ScorecardsTab mode={status.isSuccess ? status.data.mode : null} />}
        {runs.data && tab === 'quality' && (
          <QualityTab stats={runs.data.stats} runs={runs.data.runs} onOpen={(id) => openRun(id, 'quality')} />
        )}
        {tab === 'operations' && (
          <OperationsTab status={status.data ?? null} stats={runs.data?.stats ?? null} runs={runs.data?.runs ?? []} onOpen={(id) => openRun(id, 'operations')} />
        )}
      </div>
      {openRunId && <RunDetail runId={openRunId} mode={status.isSuccess ? status.data.mode : null} onClose={() => setOpenRunId(null)} />}
    </div>
  );
}
